import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { ErrorBoundary } from "./components/error-boundary";
import "./index.css";

// Self-heal stale chunk loads. After a deploy, an old tab/cached shell may try
// to import a content-hashed JS chunk that no longer exists -> the dynamic
// import rejects and the app gets stuck on the "Loading..." screen. When we
// detect this, clear caches and hard-reload ONCE (guarded so we never loop)
// to pull the fresh build.
(() => {
  const RELOAD_FLAG = "ttt-chunk-reload";
  const isChunkLoadError = (msg: string) =>
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \d+ failed/i.test(
      msg
    );

  const recover = async () => {
    if (sessionStorage.getItem(RELOAD_FLAG)) return; // already tried once
    sessionStorage.setItem(RELOAD_FLAG, "1");
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch {
      // ignore — still attempt a reload below
    }
    window.location.reload();
  };

  // Vite emits this for failed lazy/preloaded module imports.
  window.addEventListener("vite:preloadError", () => {
    void recover();
  });
  window.addEventListener("error", (e) => {
    if (e?.message && isChunkLoadError(e.message)) void recover();
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason;
    const msg = typeof reason === "string" ? reason : reason?.message || "";
    if (isChunkLoadError(msg)) void recover();
  });

  // Clear the guard once the app successfully boots so future deploys can heal.
  window.addEventListener("load", () => {
    setTimeout(() => sessionStorage.removeItem(RELOAD_FLAG), 5000);
  });
})();

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

if ("serviceWorker" in navigator) {
  const isProduction = window.location.hostname === "thirdtempleofisrael.com" || window.location.hostname === "www.thirdtempleofisrael.com";
  if (isProduction) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch((e) => console.warn("[SW] Registration failed:", e));
    });
  } else {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(r => r.unregister());
    });
  }
}
